import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { enderecoDeArquivo } from '@/shared/api/client';
import {
  signatureRequests,
  signatureSettings,
  type SignatureRequest,
  type SignatureRequestSignerInput,
  type SignatureRequestSignerRole,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate, formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import {
  ambienteDoProvedor,
  origemDoEvento,
  papelDoSignatario,
  PERMISSAO_PEDIR_ASSINATURA,
  PERMISSAO_VER_PEDIDOS,
  situacaoDoPedido,
  situacaoDoSignatario,
  tipoDoEvento,
} from './signatureRequestLabels';

/**
 * Assinatura eletronica por provedor (P-MIN-4): o pedido de um documento gerado, a
 * situacao dele e o log. Ver docs/ADR/0062-assinatura-por-provedor.md.
 *
 * O provedor e o da empresa (Assinaturas > Configuracao). Vai ao provedor o PDF do
 * documento, e a versao assinada volta para o lado da original, que nao e apagada.
 */

function abrir(url: string) {
  window.open(enderecoDeArquivo(url), '_blank', 'noopener,noreferrer');
}

export function SignatureRequestStatusBadge({ pedido }: { pedido: SignatureRequest }) {
  const estado = situacaoDoPedido[pedido.status];
  return <Badge tone={estado.tone}>{estado.label}</Badge>;
}

const papeis: { value: SignatureRequestSignerRole; label: string }[] = (
  Object.keys(papelDoSignatario) as SignatureRequestSignerRole[]
)
  .filter((papel) => papel !== 'seguradora')
  .map((papel) => ({ value: papel, label: papelDoSignatario[papel] }));

function signatarioVazio(): SignatureRequestSignerInput {
  return { name: '', email: '', document: '', role: 'tomador' };
}

/** Dialogo "Pedir assinatura": signatarios, mensagem e prazo. */
export function SignatureRequestDialog({
  documentId,
  subject,
  onClose,
  onCreated,
}: {
  documentId: string;
  subject: string;
  onClose: () => void;
  onCreated?: ((pedido: SignatureRequest) => void) | undefined;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const configuracao = useQuery({
    queryKey: ['signature-settings'],
    queryFn: ({ signal }) => signatureSettings.get(signal),
  });
  const [incluirSeguradora, setIncluirSeguradora] = useState(true);
  const [signatarios, setSignatarios] = useState<SignatureRequestSignerInput[]>([
    signatarioVazio(),
  ]);
  const [mensagem, setMensagem] = useState('');
  const [prazo, setPrazo] = useState('');

  const c = configuracao.data;
  const temPadrao = Boolean(c?.default_signer_name);
  // O pedido aceita 20 signatarios, e o da seguradora ocupa uma posicao (ADR 0079,
  // R5-B08).
  const limiteDeSignatarios = temPadrao && incluirSeguradora ? 19 : 20;
  const semProvedor = c !== undefined && (c.provider === 'nenhum' || !c.provider_ready);
  const provedor = c?.providers.find((p) => p.key === c.provider);

  const pedir = useMutation({
    mutationFn: () =>
      signatureRequests.create(documentId, {
        include_default_signer: temPadrao && incluirSeguradora,
        signers: signatarios
          .filter((s) => s.name.trim() !== '' || s.email.trim() !== '')
          .map(
            (s) =>
              ({
                ...s,
                document: s.document?.trim() || undefined,
              }) as SignatureRequestSignerInput,
          ),
        message: mensagem,
        ...(prazo.trim() !== '' ? { deadline_days: Number(prazo) } : {}),
      }),
    onSuccess: (pedido) => {
      if (pedido.status === 'erro') {
        toast.error(
          `O provedor recusou o pedido: ${pedido.failure_message || pedido.failure_code}`,
        );
      } else {
        toast.success('Pedido de assinatura enviado ao provedor.');
      }
      void queryClient.invalidateQueries({ queryKey: ['signature-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['policy'] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      onCreated?.(pedido);
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const alterar = (
    indice: number,
    campo: keyof SignatureRequestSignerInput,
    valor: string,
  ) =>
    setSignatarios((lista) =>
      lista.map((s, i) => (i === indice ? { ...s, [campo]: valor } : s)),
    );

  return (
    <Modal
      open
      onClose={onClose}
      title="Pedir assinatura"
      description={subject}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pedir.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => pedir.mutate()}
            loading={pedir.isPending}
            disabled={semProvedor || configuracao.isPending}
          >
            Enviar ao provedor
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {semProvedor ? (
          <Alert tone="warning" title="Sem provedor de assinatura">
            A empresa ainda nao escolheu um provedor ligado nesta instalacao. Escolha em
            Assinaturas &gt; Configuracao.
          </Alert>
        ) : provedor ? (
          <p className="text-xs text-ink-600">
            Provedor: <strong>{provedor.name}</strong> (
            {ambienteDoProvedor[provedor.environment] ?? provedor.environment}). O
            documento vai em PDF, e cada signatario recebe o pedido por e-mail.
          </p>
        ) : null}

        {temPadrao ? (
          <label className="flex items-center gap-2 text-sm text-ink-800">
            <input
              type="checkbox"
              checked={incluirSeguradora}
              onChange={(event) => setIncluirSeguradora(event.target.checked)}
            />
            Incluir o signatario da seguradora ({c?.default_signer_name})
          </label>
        ) : null}

        {signatarios.map((s, indice) => (
          <fieldset
            key={indice}
            className="grid gap-3 rounded-md border border-line p-3 sm:grid-cols-2"
          >
            <legend className="px-1 text-2xs font-semibold text-ink-500">
              Signatario {indice + 1}
            </legend>
            <Field
              label="Nome do signatario"
              value={s.name}
              onChange={(event) => alterar(indice, 'name', event.target.value)}
            />
            <Field
              label="E-mail do signatario"
              type="email"
              value={s.email}
              onChange={(event) => alterar(indice, 'email', event.target.value)}
            />
            <Field
              label="CPF do signatario"
              hint="Opcional. O provedor confere a identidade com ele."
              value={s.document ?? ''}
              onChange={(event) => alterar(indice, 'document', event.target.value)}
            />
            <Select
              label="Papel do signatario"
              options={papeis}
              value={s.role}
              onChange={(event) => alterar(indice, 'role', event.target.value)}
            />
            {signatarios.length > 1 ? (
              <div className="sm:col-span-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setSignatarios((lista) => lista.filter((_, i) => i !== indice))
                  }
                >
                  Retirar signatario
                </Button>
              </div>
            ) : null}
          </fieldset>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={signatarios.length >= limiteDeSignatarios}
            onClick={() => setSignatarios((lista) => [...lista, signatarioVazio()])}
          >
            Acrescentar signatario
          </Button>
          {signatarios.length >= limiteDeSignatarios ? (
            <span className="text-2xs text-ink-500">
              O pedido aceita ate 20 signatarios, contando o da seguradora.
            </span>
          ) : null}
        </div>

        <Textarea
          label="Mensagem aos signatarios"
          value={mensagem}
          maxLength={1000}
          onChange={(event) => setMensagem(event.target.value)}
        />
        <Field
          label="Prazo da assinatura em dias"
          type="number"
          min={1}
          max={90}
          hint={`Vazio usa o prazo da configuracao (${c?.deadline_days ?? 30} dias).`}
          value={prazo}
          onChange={(event) => setPrazo(event.target.value)}
        />
      </div>
    </Modal>
  );
}

/**
 * Detalhe do pedido: signatarios, log e acoes (baixar a assinada, lembrar, consultar o
 * provedor, cancelar).
 */
export function SignatureRequestDetail({ requestId }: { requestId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');

  const pedido = useQuery({
    queryKey: ['signature-requests', 'detalhe', requestId],
    queryFn: ({ signal }) => signatureRequests.get(requestId, signal),
  });
  const eventos = useQuery({
    queryKey: ['signature-requests', 'eventos', requestId],
    queryFn: ({ signal }) => signatureRequests.events(requestId, signal),
  });

  const depois = (mensagem: string) => () => {
    toast.success(mensagem);
    void queryClient.invalidateQueries({ queryKey: ['signature-requests'] });
    void queryClient.invalidateQueries({ queryKey: ['policy'] });
  };
  const falhou = (error: Error) => toast.error(error.message);
  const lembrar = useMutation({
    mutationFn: () => signatureRequests.remind(requestId),
    onSuccess: depois('Lembrete enviado aos signatarios que faltam.'),
    onError: falhou,
  });
  const consultar = useMutation({
    mutationFn: () => signatureRequests.sync(requestId),
    onSuccess: depois('Consulta ao provedor feita.'),
    onError: falhou,
  });
  // O pedido assinado sem a versao assinada busca a versao no proprio pedido, sem
  // segundo envelope (ADR 0079, R5-M01).
  const buscarVersao = useMutation({
    mutationFn: () => signatureRequests.sync(requestId),
    onSuccess: (atual) => {
      if (atual.signed) {
        toast.success('Versao assinada guardada.');
      } else {
        toast.error(
          'A versao assinada ainda nao veio do provedor. Veja o log do pedido.',
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['signature-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['policy'] });
    },
    onError: falhou,
  });
  const cancelar = useMutation({
    mutationFn: () => signatureRequests.cancel(requestId, motivo),
    onSuccess: () => {
      setCancelando(false);
      depois('Pedido cancelado no provedor.')();
    },
    onError: falhou,
  });
  const baixar = useMutation({
    mutationFn: () => signatureRequests.signedFile(requestId),
    onSuccess: (arquivo) => abrir(arquivo.url),
    onError: falhou,
  });

  if (pedido.isPending) return <p className="text-sm text-ink-500">Carregando.</p>;
  if (pedido.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar o pedido">
        {pedido.error.message}
      </Alert>
    );
  }
  const p = pedido.data;
  const podeAgir =
    can(PERMISSAO_PEDIR_ASSINATURA) &&
    p.status === 'pendente' &&
    p.external_document_id !== '';
  // A consulta tambem vale para o pedido encerrado sem todas as assinaturas: a resposta
  // com todos assinados conclui a assinatura (ADR 0079, R5-C01).
  const podeConsultar =
    can(PERMISSAO_PEDIR_ASSINATURA) &&
    p.external_document_id !== '' &&
    (p.status === 'pendente' || p.status === 'encerrado_incompleto');
  const podeBuscarVersao =
    can(PERMISSAO_PEDIR_ASSINATURA) && p.status === 'assinado' && p.signed === null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <SignatureRequestStatusBadge pedido={p} />
        {p.overdue ? <Badge tone="warning">Prazo vencido sem retorno</Badge> : null}
        <span className="text-xs text-ink-600">
          {p.provider_name} · prazo {formatDate(p.deadline_at)} ·{' '}
          {p.trigger === 'automatico'
            ? 'pedido automatico'
            : `pedido por ${p.requested_label}`}
        </span>
      </div>

      {p.status === 'erro' ? (
        <Alert tone="danger" title="O pedido nao chegou ao provedor">
          {p.failure_message || p.failure_code}
        </Alert>
      ) : null}
      {p.status === 'encerrado_incompleto' ? (
        <Alert tone="warning" title="Encerrado sem todas as assinaturas">
          O provedor encerrou o documento com signatario sem assinar. A assinatura nao
          foi concluida: a versao assinada nao e guardada e o documento nao conta como
          assinado. A prova de que todos assinaram (o aviso de conclusao do provedor ou
          a consulta) conclui o pedido.
        </Alert>
      ) : null}
      {p.status === 'cancelado' && p.failure_message ? (
        <Alert tone="info" title="Motivo do cancelamento">
          {p.failure_message}
        </Alert>
      ) : null}

      <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
        {p.signers.map((s) => (
          <li
            key={s.id}
            className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink-900">{s.name}</p>
              <p className="text-2xs text-ink-500">
                {papelDoSignatario[s.role]} · {s.email}
                {s.document_masked ? ` · CPF ${s.document_masked}` : ''}
                {s.auth === 'automatica' ? ' · assinatura automatica' : ''}
              </p>
            </div>
            <span className="text-xs text-ink-700">
              {situacaoDoSignatario[s.status] ?? s.status}
              {s.decided_at ? ` em ${formatDateTime(s.decided_at)}` : ''}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        {p.signed ? (
          <Button
            variant="secondary"
            loading={baixar.isPending}
            onClick={() => baixar.mutate()}
          >
            Baixar versao assinada
          </Button>
        ) : null}
        {podeBuscarVersao ? (
          <Button
            variant="secondary"
            loading={buscarVersao.isPending}
            onClick={() => buscarVersao.mutate()}
          >
            Buscar versao assinada
          </Button>
        ) : null}
        {podeAgir ? (
          <Button
            variant="secondary"
            loading={lembrar.isPending}
            onClick={() => lembrar.mutate()}
          >
            Lembrar signatarios
          </Button>
        ) : null}
        {podeConsultar ? (
          <Button
            variant="secondary"
            loading={consultar.isPending}
            onClick={() => consultar.mutate()}
          >
            Consultar o provedor
          </Button>
        ) : null}
        {podeAgir ? (
          <Button variant="danger" onClick={() => setCancelando(true)}>
            Cancelar pedido
          </Button>
        ) : null}
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold text-ink-700">Log da assinatura</p>
        {eventos.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar o log">
            {eventos.error.message}
          </Alert>
        ) : (
          <ol className="flex flex-col gap-2">
            {(eventos.data ?? []).map((e) => (
              <li key={e.id} className="rounded-md bg-ink-50 px-3 py-2">
                <p className="text-xs font-semibold text-ink-800">
                  {tipoDoEvento[e.kind] ?? e.kind}
                  <span className="font-normal text-ink-500">
                    {' '}
                    · {origemDoEvento[e.source] ?? e.source} ·{' '}
                    {formatDateTime(e.occurred_at)}
                    {e.actor_label ? ` · ${e.actor_label}` : ''}
                  </span>
                </p>
                {e.message ? <p className="text-xs text-ink-600">{e.message}</p> : null}
              </li>
            ))}
          </ol>
        )}
      </div>

      {cancelando ? (
        <Modal
          open
          onClose={() => setCancelando(false)}
          title="Cancelar o pedido de assinatura"
          description="O pedido e cancelado no provedor, e os signatarios deixam de poder assinar."
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setCancelando(false)}>
                Voltar
              </Button>
              <Button
                variant="danger"
                loading={cancelar.isPending}
                disabled={motivo.trim() === ''}
                onClick={() => cancelar.mutate()}
              >
                Cancelar pedido
              </Button>
            </>
          }
        >
          <Textarea
            label="Motivo do cancelamento"
            value={motivo}
            maxLength={500}
            onChange={(event) => setMotivo(event.target.value)}
          />
        </Modal>
      ) : null}
    </div>
  );
}

/**
 * Bloco de assinatura por provedor de um documento gerado: o ultimo pedido e o botao
 * "Pedir assinatura". Usado no documento da apolice e nos paineis de minuta e CCG.
 */
export function SignatureRequestDocumentBlock({
  documentId,
  subject,
  signed,
}: {
  documentId: string;
  subject: string;
  /** O documento ja tem a versao assinada: nao vai de novo ao provedor. */
  signed?: boolean | undefined;
}) {
  const { can } = useAuth();
  const [pedindo, setPedindo] = useState(false);
  const [vendo, setVendo] = useState<SignatureRequest | null>(null);
  const vePedidos = can(PERMISSAO_VER_PEDIDOS);
  const podePedir = can(PERMISSAO_PEDIR_ASSINATURA);

  const pedidos = useQuery({
    queryKey: ['signature-requests', 'documento', documentId],
    queryFn: ({ signal }) => signatureRequests.listByDocument(documentId, signal),
    enabled: vePedidos,
  });

  if (!vePedidos && !podePedir) return null;
  const ultimo = pedidos.data?.[0];
  const aberto = ultimo?.status === 'pendente';
  // O ultimo pedido assinado, com ou sem a versao assinada guardada, nao abre outro: a
  // versao que falta e buscada no proprio pedido (ADR 0079, R5-M01).
  const concluido = ultimo?.status === 'assinado';

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-line px-3 py-2">
      <span className="text-xs font-semibold text-ink-700">Assinatura eletronica</span>
      {ultimo ? (
        <>
          <SignatureRequestStatusBadge pedido={ultimo} />
          <span className="text-2xs text-ink-500">
            pedido {ultimo.number} · {ultimo.provider_name} ·{' '}
            {formatDate(ultimo.created_at)}
          </span>
          <AcaoComIcone
            acao="visualizar"
            rotulo="Ver pedido"
            onClick={() => setVendo(ultimo)}
          />
        </>
      ) : (
        <span className="text-2xs text-ink-500">Sem pedido ao provedor.</span>
      )}
      {podePedir && !aberto && !concluido && !signed ? (
        <Button size="sm" onClick={() => setPedindo(true)}>
          Pedir assinatura
        </Button>
      ) : null}

      {pedindo ? (
        <SignatureRequestDialog
          documentId={documentId}
          subject={subject}
          onClose={() => setPedindo(false)}
        />
      ) : null}
      {vendo ? (
        <Modal
          open
          onClose={() => setVendo(null)}
          title={`Pedido de assinatura ${vendo.number}`}
          description={vendo.subject_label}
          size="lg"
          footer={<Button onClick={() => setVendo(null)}>Fechar</Button>}
        >
          <SignatureRequestDetail requestId={vendo.id} />
        </Modal>
      ) : null}
    </div>
  );
}
